import React, { useEffect, useRef } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Collapse, Flex, Text, useColorModeValue } from '@chakra-ui/react';

const REVEAL_DURATION_MS = 500;
const REVEAL_BOTTOM_MARGIN_PX = 16;

// Approximates UIKit's default ease-in-out so the scroll trails the Collapse height animation.
function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function getScrollParent(element: HTMLElement): HTMLElement {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

/** Eases the scroll container so the opened content (and not past its row header) becomes visible. */
function animateRevealScroll(header: HTMLElement, content: HTMLElement): () => void {
  const container = getScrollParent(content);
  const containerRect = container.getBoundingClientRect();
  const headerTopOffset = header.getBoundingClientRect().top - containerRect.top;
  // The inner content box is laid out at full height even while Collapse clips it.
  const overflowBelow = content.getBoundingClientRect().bottom - containerRect.bottom + REVEAL_BOTTOM_MARGIN_PX;
  const distance = Math.min(overflowBelow, headerTopOffset);
  if (distance <= 0) return () => undefined;

  const startTop = container.scrollTop;
  let startTime: number | null = null;
  let frame = requestAnimationFrame(step);

  function step(now: number) {
    startTime ??= now;
    const progress = Math.min(1, (now - startTime) / REVEAL_DURATION_MS);
    container.scrollTop = startTop + distance * easeInOutCubic(progress);
    if (progress < 1) frame = requestAnimationFrame(step);
  }

  function cancel() {
    cancelAnimationFrame(frame);
    container.removeEventListener('touchstart', cancel);
    container.removeEventListener('wheel', cancel);
  }

  container.addEventListener('touchstart', cancel, { passive: true });
  container.addEventListener('wheel', cancel, { passive: true });
  return cancel;
}

interface DisclosureRowProps {
  title: string;
  isOpen: boolean;
  dividerColor: string;
  hideDivider?: boolean;
  prefersReducedMotion: boolean;
  children: React.ReactNode;
  useChildrenBorderRadius?: boolean;
  onToggle: () => void;
}

export function DisclosureRow({
  title,
  isOpen,
  dividerColor,
  hideDivider = false,
  prefersReducedMotion,
  children,
  useChildrenBorderRadius = false,
  onToggle,
}: DisclosureRowProps) {
  const textColor = useColorModeValue('gray.500', 'white');
  const contentBg = useColorModeValue('#E8E8E8', '#626469');
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const content = contentRef.current;
    const header = headerRef.current;
    if (!isOpen || !content || !header) return;

    content.focus({ preventScroll: true });
    if (prefersReducedMotion) {
      content.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
      return;
    }
    return animateRevealScroll(header, content);
  }, [isOpen, prefersReducedMotion]);

  return (
    <Box>
      <Flex
        ref={headerRef}
        as="button"
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
        position="relative"
        borderBottomLeftRadius="8px"
        borderBottomRightRadius="30px"
        borderTopRightRadius="5px"
        borderTopLeftRadius="5px"
        align="center"
        justify="space-between"
        w="100%"
        minH="67px"
        px={5}
        py={3}
        textAlign="left"
        color={textColor}
      >
        <Text pr={4}>{title}</Text>
        <ChevronDownIcon
          boxSize={5}
          color="#A6A6A6"
          transform={isOpen ? 'rotate(180deg)' : undefined}
          transition={prefersReducedMotion ? 'none' : 'transform 160ms ease'}
          flexShrink={0}
        />
        {!hideDivider && (
          <Box left={4} bottom={0} right={0} position="absolute" w="calc(100% - 1rem)" h="1px" bg={dividerColor} />
        )}
      </Flex>
      <Collapse
        in={isOpen}
        animateOpacity={!prefersReducedMotion}
        transition={prefersReducedMotion ? { enter: { duration: 0 }, exit: { duration: 0 } } : undefined}
      >
        <Box
          ref={contentRef}
          tabIndex={-1}
          _focus={{ outline: 'none' }}
          px={5}
          py={4}
          backgroundColor={contentBg}
          borderBottomLeftRadius={useChildrenBorderRadius ? '8px' : undefined}
          borderBottomRightRadius={useChildrenBorderRadius ? '30px' : undefined}
          borderTopRightRadius={useChildrenBorderRadius ? '5px' : undefined}
          borderTopLeftRadius={useChildrenBorderRadius ? '5px' : undefined}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
